import { useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const msgRef = useRef<HTMLParagraphElement>(null);

  const footerCategories = categories.slice(0, 5);
  const footerArticles = [
    { name: "Buying Guide", href: "/article/kakobuy-buying-guide" },
    { name: "Trusted Sellers", href: "/article/trusted-kakobuy-sellers" },
    { name: "Safety Guide", href: "/article/is-kakobuy-safe" },
    { name: "Shipping Guide", href: "/article/kakobuy-shipping-guide" },
    { name: "Size Guide", href: "/article/kakobuy-size-guide" },
    { name: "Quality Check", href: "/article/kakobuy-quality-check" },
    { name: "Beginner Guide", href: "/article/kakobuy-spreadsheet-guide" },
    { name: "FAQ", href: "#faq" },
  ];
  const footerLegal = [
    { name: "Privacy Policy", href: "#" },
    { name: "Terms of Service", href: "#" },
    { name: "Contact", href: "#" },
  ];

  return (
    <footer className="bg-background-100 border-t border-background-200/40" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left Section */}
          <div className="lg:w-[40%]">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
              Stay<br />Updated
            </h3>
            <p className="mt-4 text-foreground-600 text-sm leading-relaxed max-w-sm">
              Subscribe to receive the latest Kakobuy spreadsheet updates, new seller recommendations, and exclusive buying tips delivered straight to your inbox.
            </p>
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d8ughuhl2p03kijan2n0"
              method="POST"
              encType="application/x-www-form-urlencoded"
              className="mt-6 max-w-sm"
              onSubmit={(e) => {
                e.preventDefault();
                const form = e.target as HTMLFormElement;
                const honeypot = form.querySelector(".form-website-alt-field input") as HTMLInputElement;
                const el = msgRef.current;
                if (honeypot && honeypot.value.trim() !== "") {
                  if (el) {
                    el.textContent = "Thanks for subscribing!";
                    el.className = "text-accent-600 text-sm mt-2";
                  }
                  return;
                }
                const emailInput = form.querySelector("input[name='email']") as HTMLInputElement;
                if (!emailInput || !emailInput.value.trim()) {
                  if (el) {
                    el.textContent = "Please enter your email address.";
                    el.className = "text-red-500 text-sm mt-2";
                  }
                  return;
                }
                const formData = new URLSearchParams();
                formData.append("email", emailInput.value.trim());
                fetch(form.action, {
                  method: "POST",
                  headers: { "Content-Type": "application/x-www-form-urlencoded" },
                  body: formData.toString(),
                })
                  .then(() => {
                    if (el) {
                      el.textContent = "Thanks for subscribing!";
                      el.className = "text-accent-600 text-sm mt-2";
                    }
                    form.reset();
                  })
                  .catch(() => {
                    if (el) {
                      el.textContent = "Something went wrong. Please try again.";
                      el.className = "text-red-500 text-sm mt-2";
                    }
                  });
              }}
            >
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Email Address</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  required
                  className="flex-1 bg-transparent border-b border-background-300 px-1 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  className="px-5 py-2 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:bg-foreground-100/50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Subscribe
                </button>
              </div>
              <div className="form-website-alt-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              </div>
              <div id="footer-sub-msg" className="text-sm mt-2" ref={msgRef}></div>
            </form>
          </div>

          {/* Right Section */}
          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">Categories</h4>
              <ul className="space-y-2.5">
                {footerCategories.map((cat) => (
                  <li key={cat.seq}>
                    <a
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-foreground-600 hover:text-foreground-900 text-sm transition-colors"
                    >
                      {cat.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">Resources</h4>
              <ul className="space-y-2.5">
                {footerArticles.map((item) => (
                  <li key={item.name}>
                    {item.href.startsWith("/article/") ? (
                      <Link
                        to={item.href}
                        className="text-foreground-600 hover:text-foreground-900 text-sm transition-colors"
                      >
                        {item.name}
                      </Link>
                    ) : (
                      <a
                        href={item.href}
                        className="text-foreground-600 hover:text-foreground-900 text-sm transition-colors"
                      >
                        {item.name}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground-900 text-sm mb-4">Legal</h4>
              <ul className="space-y-2.5">
                {footerLegal.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className="text-foreground-600 hover:text-foreground-900 text-sm transition-colors"
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-6 border-t border-background-200/40 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-foreground-400">
            &copy; {currentYear} KakobuySpreadsheet.casa. All rights reserved.
          </p>
          <p className="text-xs text-foreground-400">
            Not affiliated with Kakobuy. Independent buyer resource.
          </p>
        </div>
      </div>
    </footer>
  );
}